import { useContext } from 'react'

import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { digObject } from '@domain-group/javascript-utils'

import {
  Button, Form, ModalContext, SelectBox, SidebarModal,
} from '@domain-group/suit-theme'

import { confirmDelete } from '@functions/entity'
import handleCallbackAction from '@functions/handleCallbackAction'

import useAgencyPaymentMethod, { useAgencyPaymentMethodForm } from '@hooks/useAgencyPaymentMethod'
import useAgencyPaymentMethods from '@hooks/useAgencyPaymentMethods'
import useAgencySelector from '@hooks/useAgencySelector'
import usePaymentMethod from '@hooks/usePaymentMethod'

const MODAL_KEY = 'CreateOrEditAgencyPaymentMethodModal'

type ModalCallbacks = {
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditAgencyPaymentMethodModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const {
    agencyPaymentMethod: initAgencyPaymentMethod,
  } = modalPayload || {}

  const { selectedAgencyId } = useAgencySelector()

  const { availablePaymentMethods } = useAgencyPaymentMethods({
    filters: { agency_id: selectedAgencyId },
    performHttpRequests: !!selectedAgencyId,
  })

  const agencyPaymentMethodPayload = useAgencyPaymentMethod(initAgencyPaymentMethod)

  const {
    agencyPaymentMethod,
    callbacks: {
      createAgencyPaymentMethod: createFn,
      deleteAgencyPaymentMethod: deleteFn,
      updateAgencyPaymentMethod: updateFn,
    },
    creating,
    paymentMethod,
    updating,
  } = agencyPaymentMethodPayload

  const agencyPaymentMethodForm = useAgencyPaymentMethodForm(agencyPaymentMethod, { validateOn: 'change' })

  const {
    entityState,
    entityState: {
      fee_type,
      fee_value,
      id,
      identity_code,
      payment_method_id,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = agencyPaymentMethodForm

  const availableAndSelectedPaymentMethods = paymentMethod.id
    ? [paymentMethod, ...availablePaymentMethods]
    : availablePaymentMethods

  const {
    paymentMethod: selectedPaymentMethod,
    processingApi,
  } = usePaymentMethod({ id: payment_method_id })

  const createOrUpdateAgencyPaymentMethodPayload = {
    callbacks: {
      action: id ? updateFn : createFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Payment Method ${id ? 'Updated' : 'Created'}`,
  }

  const confirmDeletePayload = {
    callbacks: {
      deleteFn,
      afterAction: closeModal,
    },
    strings: {
      confirmString: 'Are you sure? This will remove the payment method from this agency.',
      deleteString: 'Remove',
      entityName: 'Payment Method',
    },
  }

  return (
    <SidebarModal callbacks={callbacks} modalKey={MODAL_KEY} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="Payment" titleSecondLine="Method" />

      <SidebarModal.Content>
        <Form.Field direction="column" label="* Payment Method">
          <SelectBox
            callbacks={{
              // @ts-expect-error TS7006
              selectItem: item => setEntityState({ payment_method_id: item.key }),
            }}
            items={availableAndSelectedPaymentMethods.map(paymentMethodItem => ({
              key: paymentMethodItem.id,
              title: `${paymentMethodItem.title}`,
            }))}
            // @ts-expect-error TS2339
            selectedItemKey={selectedPaymentMethod?.id}
          />
        </Form.Field>

        {!!processingApi && (
          <Form.Field direction="column" label="Identity Code" marginTop="large">
            <input name="identity_code" type="text" value={identity_code} {...handlers} />
          </Form.Field>
        )}

        <Form.Field direction="column" errorMessage={errors.name} label="* Fee Type" marginTop="large">
          {/*
           // @ts-expect-error TS2322 */}
          <select name="fee_type" value={fee_type} {...handlers}>
            <option value="">Select Fee Type</option>
            <option key="percentage" value="percentage">Percentage</option>
            <option key="fixed" value="fixed">Fixed Value</option>
          </select>
        </Form.Field>

        <Form.Field
          direction="column"
          errorMessage={errors.fee_value}
          label={`* Fee Value (${fee_type === 'percentage' ? '%' : '$'})`}
          marginTop="large"
        >
          <input name="fee_value" type="number" value={Number(fee_value)} {...handlers} />
        </Form.Field>

        {!!id && (
          <Form.Field direction="column" label="More Options" marginTop="large">
            <Button
              buttonStyle="secondaryUtility"
              icon={<FontAwesomeIcon icon={faTrashAlt} />}
              // @ts-expect-error TS2345
              onClick={() => confirmDelete(confirmDeletePayload)}
              size="medium"
            >
              Delete Payment Method
            </Button>
          </Form.Field>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={() => handleCallbackAction(createOrUpdateAgencyPaymentMethodPayload)}
          size="large"
        >
          {saveEnabled ? `${!id ? 'Add' : 'Update'} Payment Method` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditAgencyPaymentMethodModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
